import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { FrontpageService } from '@/services/frontpage.service'
import { useLocaleStore } from '@/stores/locale.store'
import type { Locale } from '@/router/locales'

import type { Frontpage } from '@/models/frontpage'

type ResourceKey = 'frontpage'
type CacheMeta = { fetchedAt: number; ttlMs: number; locale: Locale | null }
const now = () => Date.now()

export const useFrontpageStore = defineStore('frontpage', () => {
  const localeStore = useLocaleStore()
  const frontpage = ref<Frontpage | null>(null)

  // Status
  const isLoading = ref(false) // use this only if you want to block something globally on the frontpage
  const loading = ref<Partial<Record<ResourceKey, boolean>>>({})
  const errors = ref<Partial<Record<ResourceKey, unknown>>>({})

  const hasErrors = computed(() => Object.values(errors.value).some((e) => e != null))

  // Cache
  const cache = ref<Record<ResourceKey, CacheMeta>>({
    frontpage: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null }, // 5 min (adjust if needed)
  })

  const inFlight = new Map<ResourceKey, Promise<void>>()

  function isFresh(key: ResourceKey) {
    const meta = cache.value[key]
    return (
      meta.fetchedAt > 0
      && meta.locale === localeStore.currentLocale
      && now() - meta.fetchedAt < meta.ttlMs
    )
  }

  function markFetched(key: ResourceKey) {
    cache.value[key].fetchedAt = now()
    cache.value[key].locale = localeStore.currentLocale
  }

  async function load(opts?: { force?: boolean }) {
    const key: ResourceKey = 'frontpage'
    if (!opts?.force && isFresh(key)) return

    const existing = inFlight.get(key)
    if (existing) return existing

    loading.value[key] = true
    errors.value[key] = undefined

    const p = (async () => {
      try {
        frontpage.value = await FrontpageService.get()
        markFetched(key)
      } catch (e) {
        errors.value[key] = e
        frontpage.value = null
      } finally {
        loading.value[key] = false
        inFlight.delete(key)
      }
    })()

    inFlight.set(key, p)
    return p
  }

  async function ensure(opts?: { force?: boolean }) {
    // If you want a full preloader only during app init,
    // keep global isLoading in content.store. Here we only use local state.
    isLoading.value = true
    try {
      await load(opts)
    } finally {
      isLoading.value = false
    }
  }

  function invalidate() {
    cache.value.frontpage.fetchedAt = 0
    cache.value.frontpage.locale = null
  }

  return {
    frontpage,
    isLoading,
    loading,
    errors,
    hasErrors,
    load,
    ensure,
    invalidate,
  }
})
